import { inject } from "@angular/core";
import { CanActivateFn, RedirectCommand, Router } from "@angular/router";
import { AuthService } from "../../../core/auth/services/auth.service";
import { routes } from "../../../../environments/environment.routes";
import { AlertHistorySessionService } from "../services/alert-history-session.service";

/** Query params appended by the backend login redirect (TWX-536 / TWX-510). */
export const ALERT_HISTORY_TOKEN_PARAM = "token";
export const ALERT_HISTORY_LOGOUT_URL_PARAM = "logout_url";
export const ALERT_HISTORY_API_URL_PARAM = "api_url";

/**
 * Entry gate for the Alert History app.
 *
 * Access is granted when the backend redirect supplied `token` and
 * `logout_url` query params (captured and stored on the spot, along with the
 * optional `api_url` pointing at the alerts backend), when a
 * previous visit already stored them, or when a regular app session exists
 * (internal users coming from the session app). Everyone else is sent to the
 * not found page.
 */
export const alertHistoryEntryGuard: CanActivateFn = (route, state) => {
  const alertHistorySession = inject(AlertHistorySessionService);
  const auth = inject(AuthService);
  const router = inject(Router);

  const token = route.queryParamMap.get(ALERT_HISTORY_TOKEN_PARAM);
  const logoutUrl = route.queryParamMap.get(ALERT_HISTORY_LOGOUT_URL_PARAM);
  const apiUrl = route.queryParamMap.get(ALERT_HISTORY_API_URL_PARAM);

  if (token && logoutUrl) {
    alertHistorySession.store(token, logoutUrl, apiUrl);
  }

  if (!alertHistorySession.hasSession && !auth.userSession.value) {
    return router.parseUrl(`/${routes.frontend.notfound}`);
  }

  if (token || logoutUrl || apiUrl) {
    // Strip the entry params so the address bar stays clean, replacing the
    // URL instead of adding a browser history entry.
    const cleanUrl = router.parseUrl(state.url);
    delete cleanUrl.queryParams[ALERT_HISTORY_TOKEN_PARAM];
    delete cleanUrl.queryParams[ALERT_HISTORY_LOGOUT_URL_PARAM];
    delete cleanUrl.queryParams[ALERT_HISTORY_API_URL_PARAM];

    return new RedirectCommand(cleanUrl, { replaceUrl: true });
  }

  return true;
};
